import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import clsx from 'clsx'

import { LogoMobile } from '@/common/assets/svg'
import { APP_ROUTES } from '@/common/constants'
import { AccountType } from '@/common/types'
import { NavigationMenuItemClickedAnalytics } from '@/common/analytics/events/NavigationMenuItemClickedAnalytics'
import { ADVISER_NAV_ITEMS } from '@/adviser/adviserNavConfig'
import { isAdviserWorkspaceRoute, isPrimaryAdviserContext } from './accountRoutes'
import useAccountStore from '@/account/accountStore'
import useAccountSwitcher from '@/account/useAccountSwitcher'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'

export function AdviserDesktopSideNav() {
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const switchAccount = useAccountSwitcher()
    const primaryAccount = usePrimaryAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)

    const handleNavigation = async (to: string) => {
        const isAdviserInternalRoute = isAdviserWorkspaceRoute(to)
        if (isAdviserInternalRoute && primaryAccount?.accountType === AccountType.ADVISER) {
            if (primaryAccount.accountId !== activeAccountId) {
                // Move to route first; switchAccount owns the full switch-session lifecycle.
                navigate(to)
                await switchAccount(primaryAccount.accountId)
                return
            }
        }
        navigate(to)
    }

    return (
        <nav className="w-16 h-screen bg-dust-200 flex flex-col">
            {/* Logo: vertically centred in the same h-20 band as the top nav bar */}
            <div className="h-20 flex-shrink-0 flex items-center justify-center">
                <NavLink to={APP_ROUTES.CLIENTS} aria-label="SIX clients" className="text-brand-primary-forest">
                    <LogoMobile className="w-6 h-6" />
                </NavLink>
            </div>

            {/* Nav items */}
            <div className="flex flex-col items-center gap-4 px-2 pt-4 flex-1 w-full">
                {ADVISER_NAV_ITEMS.map((item) => {
                    const Icon = item.icon
                    // Determine active state taking account context into consideration.
                    const isAccountItem = item.key === 'account'
                    const primaryContext = isPrimaryAdviserContext({ primaryAccount, activeAccountId })
                    const isActive = isAccountItem
                        ? pathname.startsWith(item.to) && primaryContext
                        : pathname.startsWith(item.to)

                    return (
                        <NavLink
                            key={item.key}
                            to={item.to}
                            onClick={(e) => {
                                e.preventDefault()
                                NavigationMenuItemClickedAnalytics.track({ menu_name: item.key, user_type: 'adviser' })
                                void handleNavigation(item.to)
                            }}
                            className="w-full rounded-lg flex flex-col justify-center items-center"
                        >
                            <div
                                className={clsx('w-full py-2 rounded-full flex justify-center items-center', {
                                    'bg-brand-accent-soft-lime': isActive,
                                    'hover:bg-lime-50': !isActive,
                                })}
                            >
                                <Icon className="w-4 h-4 text-brand-primary-forest" />
                            </div>
                            <div className="w-10 text-center text-brand-primary-forest text-xs font-normal leading-5">
                                {item.label}
                            </div>
                        </NavLink>
                    )
                })}
            </div>
        </nav>
    )
}
